import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './MarkdownRenderer.css';

const MarkdownRenderer = ({ content, className = '' }) => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [isCodeBlockOpen, setIsCodeBlockOpen] = useState(false);

  if (!content) return null;

  const handleCopy = async (code, language) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(language);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Ошибка копирования:', err);
    }
  };

  const handleDownload = (code, language, fileName) => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `code.${language || 'txt'}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`markdown-renderer ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // —— Блоки кода с подсветкой и кнопками ——
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const code = String(children).replace(/\n$/, '');

            if (!inline && language) {
              return (
                <div className="code-block-wrapper">
                  <div className="code-block-header">
                    <span className="code-block-language">{language}</span>
                    <div className="code-block-actions">
                      <button
                        className="code-action-btn"
                        onClick={() => handleCopy(code, language)}
                        title="Скопировать код"
                      >
                        {copiedCode === language ? '✅' : '📋'}
                      </button>
                      <button
                        className="code-action-btn"
                        onClick={() => handleDownload(code, language)}
                        title="Скачать код"
                      >
                        ⬇️
                      </button>
                    </div>
                  </div>
                  <SyntaxHighlighter
                    language={language}
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0,
                      borderRadius: '0 0 6px 6px',
                      padding: '12px 16px',
                      background: '#1e1e1e',
                    }}
                    {...props}
                  >
                    {code}
                  </SyntaxHighlighter>
                </div>
              );
            }

            return (
              <code className="inline-code" {...props}>
                {children}
              </code>
            );
          },
          // —— Параграфы с обработкой упоминаний ——
          p({ children }) {
            if (typeof children === 'string') {
              const parts = children.split(/(@[\p{L}\p{N}_]+)/gu);
              return (
                <p className="markdown-paragraph">
                  {parts.map((part, index) => {
                    if (part.startsWith('@')) {
                      return <span key={index} className="mention">{part}</span>;
                    }
                    return part;
                  })}
                </p>
              );
            }
            return <p className="markdown-paragraph">{children}</p>;
          },
          // —— Ссылки ——
          a({ href, children }) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" className="markdown-link">
                {children}
              </a>
            );
          },
          // —— Заголовки ——
          h2({ children }) {
            return <h2 className="markdown-heading">{children}</h2>;
          },
          h3({ children }) {
            return <h3 className="markdown-heading">{children}</h3>;
          },
          h4({ children }) {
            return <h4 className="markdown-heading">{children}</h4>;
          },
          // —— Списки ——
          ul({ children }) {
            return <ul className="markdown-list">{children}</ul>;
          },
          ol({ children }) {
            return <ol className="markdown-list">{children}</ol>;
          },
          li({ children }) {
            return <li className="markdown-list-item">{children}</li>;
          },
          // —— Цитаты ——
          blockquote({ children }) {
            return <blockquote className="markdown-blockquote">{children}</blockquote>;
          },
          // —— Таблицы ——
          table({ children }) {
            return <table className="markdown-table">{children}</table>;
          },
          th({ children }) {
            return <th className="markdown-table-header">{children}</th>;
          },
          td({ children }) {
            return <td className="markdown-table-cell">{children}</td>;
          },
          // —— Горизонтальные линии ——
          hr() {
            return <hr className="markdown-hr" />;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;